import { useEffect, useState } from "react";
import { Archive, LayoutGrid, List, Search, X } from "lucide-react";
import { ASSET_TYPE_LABEL } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";

// Phase 7 sub-bucket 13. Content list toolbar — debounced search +
// asset-type chip filter + archived toggle + grid/list switch. State lives
// in URL search params on the parent so the toolbar is a pure controlled
// component.
//
// Search debounce mirrors the legacy ContentToolbar's 250ms (the legacy
// debounced via useEffect; we use useDebouncedCallback for the stable
// closure shape Phase 6/7 has standardised on).
const TYPE_CHIPS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "folder", label: "Folder" },
  { value: "deck", label: ASSET_TYPE_LABEL.deck },
  { value: "video_link", label: ASSET_TYPE_LABEL.video_link },
  { value: "case_study", label: ASSET_TYPE_LABEL.case_study },
  { value: "battlecard", label: ASSET_TYPE_LABEL.battlecard },
  { value: "one_pager", label: ASSET_TYPE_LABEL.one_pager },
  { value: "playbook", label: ASSET_TYPE_LABEL.playbook },
  { value: "template", label: ASSET_TYPE_LABEL.template },
  { value: "other", label: ASSET_TYPE_LABEL.other },
];

export function ContentToolbar({
  q,
  type,
  archived,
  view,
  onChange,
}: {
  q: string;
  type: string | null;
  archived: boolean;
  view: "grid" | "list";
  onChange: (key: string, value: string | null) => void;
}) {
  // Local search state — the URL update is debounced to keep typing snappy.
  const [localQ, setLocalQ] = useState(q);
  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const commitQ = useDebouncedCallback((next: string) => {
    onChange("q", next.length > 0 ? next : null);
  }, 250);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={localQ}
          placeholder="Search by title"
          onChange={(e) => {
            const v = e.target.value;
            setLocalQ(v);
            commitQ(v);
          }}
          className="pl-8"
        />
      </div>

      <div className="flex items-center gap-1 rounded-md border border-border/70 bg-card/40 px-1.5 py-1">
        <span className="px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Type
        </span>
        {TYPE_CHIPS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => onChange("type", type === c.value ? null : c.value)}
            className={cn(
              "rounded px-2 py-0.5 text-[11px] font-medium transition-colors",
              type === c.value
                ? "bg-accent/15 text-accent"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      <Button
        type="button"
        variant={archived ? "default" : "outline"}
        size="sm"
        onClick={() => onChange("archived", archived ? null : "1")}
        className="h-8"
      >
        <Archive className="mr-1.5 h-3.5 w-3.5" />
        {archived ? "Showing archived" : "Show archived"}
      </Button>

      <div className="inline-flex items-center rounded-md border border-border/70 bg-card/40 p-0.5">
        <button
          type="button"
          aria-label="Grid view"
          aria-pressed={view === "grid"}
          onClick={() => onChange("view", "grid")}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded transition-colors",
            view === "grid"
              ? "bg-accent/15 text-accent"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="List view"
          aria-pressed={view === "list"}
          onClick={() => onChange("view", "list")}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded transition-colors",
            view === "list"
              ? "bg-accent/15 text-accent"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <List className="h-3.5 w-3.5" />
        </button>
      </div>

      {(q || type || archived) && (
        <button
          type="button"
          onClick={() => {
            setLocalQ("");
            commitQ.cancel();
            onChange("q", null);
            onChange("type", null);
            onChange("archived", null);
          }}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3 w-3" />
          Clear filters
        </button>
      )}
    </div>
  );
}
